import Color from 'color';
import { lFColor, mobileRes } from 'pages/components/LFHeader/LFHeader.styled';
import { overlayZIndex } from 'pages/components/Modal/Modal.styled';
import styled from 'styled-components';

/**
 * Above in-app Loader (overlayZIndex + 100). Keeps the strip undimmed
 * without offsetting the loader in Y — dim stays full-bleed behind the alert.
 */
const layoutAlertZIndex = overlayZIndex + 150;
/** Under RightBar, header, page overlays, and the in-app loader. */
const layoutAlertBelowOverlaysZIndex = 1;

export const Container = styled.div<{
  $accent: string;
  $belowOverlays?: boolean;
  $paleStrip?: boolean;
}>`
  position: relative;
  z-index: ${({ $belowOverlays }) => (
    $belowOverlays ? layoutAlertBelowOverlaysZIndex : layoutAlertZIndex
  )};
  width: 100%;
  min-height: 58px;
  background: ${({ $accent, $paleStrip }) => (
    $paleStrip
      ? Color($accent).mix(Color('#ffffff'), 0.78).toString()
      : Color($accent).lighten(0.72).toString()
  )};
  border-bottom: 1px solid ${({ $accent, $paleStrip }) => (
    $paleStrip
      ? Color($accent).mix(Color('#ffffff'), 0.64).toString()
      : Color($accent).lighten(0.5).toString()
  )};
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  color: ${({ $accent }) => Color($accent).darken(0.35).toString()};
  flex-shrink: 0;
  @media (max-width: ${mobileRes}) {
    grid-template-columns: auto 1fr;
    align-items: start;
    gap: 8px 10px;
    padding: 10px 12px;
  }
`;

export const Icon = styled.div<{ $accent: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $accent }) => Color($accent).alpha(0.14).toString()};
  color: ${({ $accent }) => $accent};
`;

export const Body = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
`;

export const Text = styled.div<{ $ellipsis?: boolean }>`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  opacity: 0.92;
  ${({ $ellipsis }) => ($ellipsis ? `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  ` : 'overflow-wrap: anywhere;')}
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
  @media (max-width: ${mobileRes}) {
    grid-column: 1 / -1;
    width: 100%;
    justify-content: stretch;
  }
`;

const actionBtnBase = `
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  @media (max-width: ${mobileRes}) {
    flex: 1;
    min-width: 0;
    height: 34px;
  }
`;

export const SecondaryBtn = styled.button<{ $accent: string }>`
  ${actionBtnBase}
  border: 1.5px solid ${({ $accent }) => Color($accent).alpha(0.35).toString()};
  background: transparent;
  color: inherit;
  &:hover {
    background: ${({ $accent }) => Color($accent).alpha(0.08).toString()};
  }
`;

export const FilledBtn = styled.button<{ $accent: string }>`
  ${actionBtnBase}
  padding: 0 12px;
  border: none;
  background: ${({ $accent }) => $accent};
  color: white;
  &:hover {
    background: ${({ $accent }) => Color($accent).darken(0.08).toString()};
  }
`;

export const OutlinedBtn = styled.button`
  ${actionBtnBase}
  border: 1.5px solid ${Color(lFColor).alpha(0.35).toString()};
  background: white;
  color: ${lFColor};
  &:hover {
    background: ${Color(lFColor).alpha(0.06).toString()};
  }
`;
